/**
 * SuperAdminPage Component
 * Platform-level administration hub for managing multiple tenants
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Shield,
  ChevronRight,
  FileText,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { Card, Button, LoadingSpinner } from '../../components/common';

// Tab components
import { DashboardTab } from './tabs/DashboardTab';
import { TenantsTab } from './tabs/TenantsTab';
import { AuditLogsTab } from './tabs/AuditLogsTab';

type SuperAdminTab = 'dashboard' | 'tenants' | 'audit-logs';

interface TabConfig {
  id: SuperAdminTab;
  name: string;
  icon: React.ElementType;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'dashboard',
    name: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Platform analytics and metrics',
  },
  {
    id: 'tenants',
    name: 'Tenants',
    icon: Building2,
    description: 'Manage client organizations',
  },
  {
    id: 'audit-logs',
    name: 'Audit Logs',
    icon: FileText,
    description: 'Security and activity logs',
  },
];

export const SuperAdminPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get active tab from URL hash or default to 'dashboard'
  const getActiveTab = (): SuperAdminTab => {
    const hash = location.hash.replace('#', '');
    if (tabs.some((t) => t.id === hash)) {
      return hash as SuperAdminTab;
    }
    return 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<SuperAdminTab>(getActiveTab());

  // Check if user is authenticated
  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Check if user has super_admin access
  if (user.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-danger-100 rounded-full flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-danger-600" />
            </div>
            <h2 className="text-2xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Access Denied</h2>
            <p className="text-stone-600 mb-6">
              Only Super Administrators can access the platform management panel.
            </p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleTabChange = (tabId: SuperAdminTab) => {
    setActiveTab(tabId);
    window.history.replaceState(null, '', `#${tabId}`);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardTab />;
      case 'tenants':
        return <TenantsTab />;
      case 'audit-logs':
        return <AuditLogsTab />;
      default:
        return <DashboardTab />;
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header - Warm gradient matching design system */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-6 lg:px-8 py-8">
          <div className="flex items-center gap-2 text-sm text-stone-300 mb-2">
            <span>Platform Admin</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">{tabs.find((t) => t.id === activeTab)?.name}</span>
          </div>
          <h1 className="text-3xl font-semibold text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Platform Management</h1>
          <p className="mt-1 text-sm text-stone-300">
            Manage tenants, monitor platform health, and control subscription plans
          </p>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Tab Navigation - Compact sidebar */}
          <div className="lg:w-56 flex-shrink-0">
            <nav className="space-y-1 lg:sticky lg:top-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all',
                      isActive
                        ? 'bg-primary-50 text-primary-700 border-l-4 border-primary-500'
                        : 'text-stone-700 hover:bg-sand border-l-4 border-transparent'
                    )}
                  >
                    <Icon
                      className={clsx(
                        'w-5 h-5 flex-shrink-0',
                        isActive ? 'text-primary-500' : 'text-stone-400'
                      )}
                    />
                    <div className="min-w-0">
                      <p
                        className={clsx(
                          'font-medium truncate',
                          isActive ? 'text-primary-700' : 'text-stone-900'
                        )}
                      >
                        {tab.name}
                      </p>
                      <p className="text-xs text-stone-500 truncate hidden lg:block">
                        {tab.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content - Full width */}
          <div className="flex-1 min-w-0">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminPage;
